import { useContext, useTransition } from 'react';
import { OverlayTriggerStateContext } from 'react-aria-components';
import type { Selection } from 'react-stately';

import type {
  MaintainerComboBoxItem,
  MemberItem,
  MultipleMaintainerProps,
  SingleMaintainerProps,
  TeamItem,
} from './types';

type MaintainerSelectionParams = (SingleMaintainerProps | MultipleMaintainerProps) & {
  itemsInSections: MaintainerComboBoxItem[];
  closeOnSelection?: boolean;
};

export function useMaintainerSelection(props: MaintainerSelectionParams) {
  const { selectionMode, closeOnSelection, itemsInSections } = props;
  const overlayState = useContext(OverlayTriggerStateContext);
  const [isPending, startTransition] = useTransition();

  // Extract the appropriate IDs based on selectionMode
  const maintainerId = selectionMode === 'single' ? props.maintainerId : undefined;
  const maintainerTeamKey = selectionMode === 'single' ? props.maintainerTeamKey : undefined;

  // Helper function to find an item across all sections
  const findItemById = (id: string): MemberItem | TeamItem | undefined => {
    for (const section of itemsInSections) {
      const foundItem = section.children.find((item) => item.id === id);
      if (foundItem) {
        return foundItem;
      }
    }
    return undefined;
  };

  const handleSingleSelectionChange = (selection: Selection, singleSelectionProps: SingleMaintainerProps) => {
    const { onMemberSelectionChange, onTeamSelectionChange, onSelectionChange } = singleSelectionProps;

    if (typeof selection === 'string') {
      return;
    }

    if (selection.size === 0) {
      if (maintainerId !== undefined && onMemberSelectionChange !== undefined) {
        return startTransition(() => {
          onMemberSelectionChange(undefined, undefined);
        });
      }

      if (maintainerTeamKey !== undefined && onTeamSelectionChange !== undefined) {
        return startTransition(() => {
          onTeamSelectionChange(undefined, undefined);
        });
      }
      if (maintainerTeamKey !== undefined && onSelectionChange !== undefined) {
        return startTransition(() => {
          onSelectionChange(undefined);
        });
      }
      return;
    }

    const [key] = Array.from(selection);
    const item = findItemById(String(key));

    if (!item) {
      return;
    }

    if (item.type === 'member' && onMemberSelectionChange !== undefined) {
      startTransition(() => {
        onMemberSelectionChange(item.id, item.resource);
      });
    } else if (item.type === 'team' && onTeamSelectionChange !== undefined) {
      startTransition(() => {
        onTeamSelectionChange(item.id, item.resource);
      });
    } else if (onSelectionChange !== undefined) {
      startTransition(() => {
        onSelectionChange(item);
      });
    }

    if (closeOnSelection && overlayState) {
      overlayState.close();
    }
  };

  const handleMultipleSelectionChange = (selection: Selection, multipleSelectionProps: MultipleMaintainerProps) => {
    const { onMemberSelectionChange, onTeamSelectionChange, onSelectionChange } = multipleSelectionProps;

    const selectedKeys = Array.from(selection);
    const selectedMembers: MemberItem[] = [];
    const selectedTeams: TeamItem[] = [];

    for (const key of selectedKeys) {
      const item = findItemById(String(key));
      if (item?.type === 'member') {
        selectedMembers.push(item);
      } else if (item?.type === 'team') {
        selectedTeams.push(item);
      }
    }

    if (onMemberSelectionChange !== undefined) {
      startTransition(() => {
        onMemberSelectionChange(
          selectedMembers.map((m) => m.id),
          selectedMembers.map((m) => m.resource),
        );
      });
    }

    if (onTeamSelectionChange !== undefined) {
      startTransition(() => {
        onTeamSelectionChange(
          selectedTeams.map((t) => t.id),
          selectedTeams.map((t) => t.resource),
        );
      });
    }
    if (onSelectionChange !== undefined) {
      const members = selectedMembers.map((m) => ({ id: m.id, type: 'member' as const, resource: m.resource }));
      const teams = selectedTeams.map((t) => ({ id: t.id, type: 'team' as const, resource: t.resource }));

      startTransition(() => {
        onSelectionChange([...members, ...teams]);
      });
    }
  };

  const handleSelectionChange = (selection: Selection) => {
    if (selection === 'all') {
      return;
    }

    if (selectionMode === 'single') {
      handleSingleSelectionChange(selection, props);
    } else {
      handleMultipleSelectionChange(selection, props);
    }
  };

  return { handleSelectionChange, isPending };
}
